import React from "react";
import { motion } from "framer-motion";

const HERO_IMG = "/images/elena/elena-hero-bw.png";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-36 md:pt-44 pb-20 md:pb-28 bg-[#F9F8F6] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 order-2 lg:order-1"
        >
          <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#3E7373]" />
            Independent Consultancy
          </p>
          <h1 className="font-serif-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight text-[#0D0D0D] font-medium">
            Operational risk
            <br />
            expertise, built for
            <br />
            <span className="italic text-[#3E7373]">modern organisations.</span>
          </h1>
          <p className="font-sans-body text-base md:text-lg text-[#4A4A4A] mt-8 max-w-xl leading-relaxed">
            I help boards and executives turn operational risk into clarity, capability and competitive advantage — drawing on three decades inside global financial services.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center bg-[#0D0D0D] text-white px-8 py-4 font-sans-body text-sm tracking-widest-2 uppercase hover:bg-[#3E7373]"
            >
              Work with me
            </a>
            <a
              href="#services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center border border-[#0D0D0D] text-[#0D0D0D] px-8 py-4 font-sans-body text-sm tracking-widest-2 uppercase hover:bg-[#0D0D0D] hover:text-white"
            >
              Explore services
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-md gap-6">
            <Stat value="30+" label="Years in financial services" />
            <Stat value="40+" label="Boards advised" />
            <Stat value="18" label="Jurisdictions" />
          </div>
        </motion.div>

        {/* Right portrait */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
          className="lg:col-span-5 order-1 lg:order-2 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-[480px] mx-auto">
            <div className="absolute -inset-4 border border-[#9CDBD9]/40 translate-x-4 translate-y-4" aria-hidden="true" />
            <img
              src={HERO_IMG}
              alt="Portrait of Elena Pykhova"
              className="relative w-full h-full object-cover object-top"
              data-testid="hero-portrait"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="480"
              height="600"
              sizes="(max-width: 1024px) 100vw, 40vw"
            />
            <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:max-w-[78%] bg-[#F9F8F6]/95 backdrop-blur-sm px-5 py-4 border-l-2 border-[#9CDBD9]">
              <p className="font-serif-display italic text-[#0D0D0D] text-lg leading-snug">
                “Risk is not a barrier. It is the language of better decisions.”
              </p>
              <p className="font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mt-2">
                — Elena Pykhova
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <p className="font-serif-display text-3xl md:text-4xl text-[#0D0D0D] font-medium leading-none">
      {value}
    </p>
    <p className="font-sans-body text-[11px] tracking-widest-2 uppercase text-[#4A4A4A] mt-2 leading-snug">
      {label}
    </p>
  </div>
);

export default Hero;
